import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  AppBar,
  Toolbar,
  Box,
  CssBaseline,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TableHead,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import TimeZoneSelector from "./components/TimeZoneSelector";
import moment from "moment-timezone";
import Footer from "./components/Footer";

function App() {
  const [selectedTimezones, setSelectedTimezones] = useState([
    "Europe/Paris", // We'll only store additional timezones here
  ]);
  const [userTime, setUserTime] = useState("");
  const [timeList, setTimeList] = useState([]);
  const [is12HourFormat, setIs12HourFormat] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [error, setError] = useState(false);
  const [inputTimezone, setInputTimezone] = useState(moment.tz.guess());
  const [isISOInput, setIsISOInput] = useState(false);
  const [isoValue, setIsoValue] = useState("");

  const WORKING_HOURS_START = 9;
  const WORKING_HOURS_END = 17;
  const MAX_TIMEZONES = 6;

  // Popular city timezones for quick selection
  const popularTimezones = [
    { city: "UTC (Coordinated Universal Time)", zone: "UTC" },
    { city: "EST/EDT (US Eastern)", zone: "America/New_York" },
    { city: "PST/PDT (US Pacific)", zone: "America/Los_Angeles" },
    { city: "GMT (London)", zone: "Europe/London" },
    { city: "CET/CEST (Central European)", zone: "Europe/Paris" },
    { city: "CST (China)", zone: "Asia/Shanghai" },
    { city: "IST (India)", zone: "Asia/Kolkata" },
    { city: "JST (Japan)", zone: "Asia/Tokyo" },
    { city: "AEST/AEDT (Sydney)", zone: "Australia/Sydney" },
    { city: "BRT (Brasilia)", zone: "America/Sao_Paulo" },
    // Keep some major cities for quick access
    { city: "Dubai", zone: "Asia/Dubai" },
    { city: "Singapore", zone: "Asia/Singapore" },
    { city: "Moscow", zone: "Europe/Moscow" },
  ];

  useEffect(() => {
    // Initialize with current time in user's local timezone
    const currentLocalTime = moment().format("YYYY-MM-DDTHH:mm");
    setUserTime(currentLocalTime);
    setError(false);
  }, []); // Remove fromTimeZone dependency as it's no longer used

  const handleTimeInput = (value, isISO = false) => {
    try {
      if (isISO) {
        // Handle ISO string input
        const parsed = moment(value);
        if (parsed.isValid()) {
          // If the input ends with 'Z', it's UTC
          if (value.endsWith("Z")) {
            setInputTimezone("UTC");
          }
          // Convert to the format expected by datetime-local
          setUserTime(parsed.format("YYYY-MM-DDTHH:mm"));
          setIsoValue(value);
          setError(false);
        } else {
          setError(true);
        }
      } else {
        // Handle datetime-local input
        setUserTime(value);
        setIsoValue(moment(value).format());
        setError(false);
      }
    } catch (e) {
      setError(true);
    }
  };

  // Update the updateTimeList callback to include inputTimezone
  const updateTimeList = useCallback(() => {
    const isWorkingHour = (time) => {
      const hour = time.hour();
      return hour >= WORKING_HOURS_START && hour < WORKING_HOURS_END;
    };

    const baseTime = userTime ? moment.tz(userTime, inputTimezone) : moment();
    const times = [];
    const allTimezones = [inputTimezone, ...selectedTimezones];

    for (let i = 0; i < 24; i++) {
      const timePoint = baseTime.clone().add(i, "hours");
      const row = {
        hour: i,
        zones: allTimezones.map((zone) => ({
          time: timePoint.tz(zone).format(is12HourFormat ? "hh:mm A" : "HH:mm"),
          date: timePoint.tz(zone).format("ddd, MMM D"),
          isWorkingHours: isWorkingHour(timePoint.tz(zone)),
        })),
      };
      times.push(row);
    }
    setTimeList(times);
  }, [
    userTime,
    selectedTimezones,
    is12HourFormat,
    inputTimezone,
    WORKING_HOURS_START,
    WORKING_HOURS_END,
  ]);

  const convertTime = () => {
    if (userTime) {
      updateTimeList();
    } else {
      setError(true);
    }
  };

  useEffect(() => {
    updateTimeList();
  }, [updateTimeList]); // Now updateTimeList includes all necessary dependencies

  // Modify handleAddTimezone to not add if it's the same as inputTimezone
  const handleAddTimezone = (timezone) => {
    if (
      selectedTimezones.length < MAX_TIMEZONES - 1 && // -1 because input timezone is always present
      !selectedTimezones.includes(timezone) &&
      timezone !== inputTimezone
    ) {
      setSelectedTimezones([...selectedTimezones, timezone]);
    }
  };

  // Modify handleRemoveTimezone to only work on additional timezones
  const handleRemoveTimezone = (indexToRemove) => {
    setSelectedTimezones(
      selectedTimezones.filter((_, index) => index !== indexToRemove)
    );
  };

  return (
    <div>
      <CssBaseline />
      <AppBar
        position="sticky"
        sx={{
          background: "linear-gradient(to right, #26a69a, #E4EDECFF)",
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            World Clock Convert
          </Typography>
          <IconButton>
            <StarBorderIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container>
        <Box sx={{ mb: 3, mt: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" sx={{ ml: -1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isISOInput}
                    onChange={() => setIsISOInput((prev) => !prev)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isISOInput ? (
                      <>
                        <ContentPasteIcon fontSize="small" />
                        Paste ISO Format
                      </>
                    ) : (
                      "Date Picker"
                    )}
                  </Box>
                }
              />
              <Tooltip
                title={
                  <div>
                    <p>
                      Toggle this switch to paste ISO format timestamps like:
                    </p>
                    <p>• 2025-03-22T14:30:00Z (UTC time)</p>
                    <p>• 2025-03-22T14:30:00+01:00 (with offset)</p>
                    <p>• 2025-03-22T14:30:00 (timezone from selector)</p>
                  </div>
                }
                placement="right"
              >
                <IconButton size="small" color="primary">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="textSecondary">
                {isISOInput && isoValue.endsWith("Z")
                  ? "Time will be interpreted as UTC (indicated by 'Z' suffix)"
                  : `Enter time in ${inputTimezone.replace("_", " ")} timezone`}
              </Typography>
            </Stack>

            {isISOInput && (
              <Typography variant="body2" color="textSecondary">
                You can now paste ISO format timestamps directly. The time will
                be interpreted based on the 'Z' suffix (UTC) or selected
                timezone.
              </Typography>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Box flex={2}>
                {isISOInput ? (
                  <TextField
                    label="Paste ISO Timestamp"
                    fullWidth
                    value={isoValue}
                    onChange={(e) => handleTimeInput(e.target.value, true)}
                    helperText={
                      error
                        ? "Invalid ISO format. Example: 2025-03-22T14:30:00Z"
                        : "Example: 2025-03-22T14:30:00Z"
                    }
                    error={error}
                    placeholder="2025-03-22T14:30:00Z"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Paste ISO timestamp here">
                          <ContentPasteIcon color="action" sx={{ mr: 1 }} />
                        </Tooltip>
                      ),
                    }}
                  />
                ) : (
                  <TextField
                    label="Select Date & Time"
                    type="datetime-local"
                    fullWidth
                    value={userTime}
                    onChange={(e) => handleTimeInput(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    onBlur={convertTime}
                  />
                )}
              </Box>
              <Box flex={1}>
                <TimeZoneSelector
                  label="Input Time Zone"
                  value={inputTimezone}
                  onChange={setInputTimezone}
                  id="input-timezone"
                  helperText={
                    isISOInput
                      ? "Timezone used when no UTC ('Z') suffix"
                      : "Select the timezone of your input time"
                  }
                  disabled={isISOInput && isoValue.endsWith("Z")}
                />
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Time format switch */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={is12HourFormat}
                onChange={() => setIs12HourFormat((prev) => !prev)}
              />
            }
            label={is12HourFormat ? "12 Hour Format" : "24 Hour Format"}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          {/* Popular Timezones and Custom Add in Two Columns */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Add Timezones to Compare
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              {/* Left Column - Quick Add Chips */}
              <Box flex={2}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Quick Add Popular Timezones:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    p: 2,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    minHeight: "160px",
                  }}
                >
                  {popularTimezones.map((tz) => (
                    <Tooltip key={tz.zone} title={`Add ${tz.city}`}>
                      <span>
                        <Chip
                          label={tz.city}
                          onClick={() => handleAddTimezone(tz.zone)}
                          disabled={selectedTimezones.includes(tz.zone)}
                          icon={<AddIcon />}
                          sx={{ mb: 1 }}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </Box>
              </Box>

              {/* Right Column - Custom Timezone Selector */}
              <Box flex={1}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Add Custom Timezone:
                </Typography>
                {selectedTimezones.length < MAX_TIMEZONES ? (
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      minHeight: "160px",
                    }}
                  >
                    <TimeZoneSelector
                      label="Search by City or Region"
                      value=""
                      onChange={handleAddTimezone}
                      id="add-timezone"
                      helperText={`Add up to ${MAX_TIMEZONES} timezones for comparison (${
                        MAX_TIMEZONES - selectedTimezones.length
                      } remaining)`}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      border: "1px dashed rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "160px",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Maximum number of timezones reached ({MAX_TIMEZONES})
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>

          {/* Enhanced Time Zone Table */}
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 650 }}
              aria-label="timezone comparison table"
            >
              <TableHead>
                <TableRow>
                  {/* Input Timezone Column */}
                  <TableCell
                    key="input-timezone"
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      position: "relative",
                      p: 2,
                      backgroundColor: "rgba(38, 166, 154, 0.05)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {inputTimezone.split("/").pop().replace("_", " ")}
                      <Tooltip title="This is your input timezone">
                        <InfoIcon fontSize="small" sx={{ opacity: 0.7 }} />
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {/* Additional Timezones */}
                  {selectedTimezones.map((zone, index) => (
                    <TableCell
                      key={zone}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        position: "relative",
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        {zone.split("/").pop().replace("_", " ")}
                        <Tooltip title="Remove timezone">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTimezone(index)}
                            sx={{
                              opacity: 0.7,
                              "&:hover": {
                                opacity: 1,
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {timeList.map((row, index) => (
                  <TableRow
                    key={index}
                    onMouseEnter={() => setHoveredRow(index)}
                    sx={{
                      backgroundColor:
                        hoveredRow === index ? "#f5f5f5" : "transparent",
                    }}
                  >
                    {row.zones.map((zone, zoneIndex) => (
                      <TableCell
                        key={`${index}-${zoneIndex}`}
                        align="center"
                        sx={{
                          backgroundColor: zone.isWorkingHours
                            ? "rgba(38, 166, 154, 0.1)"
                            : "inherit",
                          borderLeft:
                            zoneIndex > 0
                              ? "1px solid rgba(224, 224, 224, 1)"
                              : "none",
                        }}
                      >
                        <Typography variant="body1">{zone.time}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {zone.date}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
      <Footer />
    </div>
  );
}

export default App;
